import React from 'react'
import classnames from 'classnames/bind'

import { RowDeprecated } from '@caudena/core/ui-kit'
import { Score } from '@caudena/core/ui-kit'
import { formatNumber } from '@caudena/core/utils/format'

import styles from './Exposure.scss'
import {
  enterprise,
  TreemapChart,
  TreemapDataItem,
  TreemapTooltipComponentProps,
} from '../ui-kit'
import { ThemeProvider } from '@emotion/react'
import { Typography } from '@caudena/core/ui-kit'
import { useInternationalizationCore } from '../internationalization'
import { InternationalizationProviderCore } from '../internationalization/internationalization.context'
import { CORE_LANG_NAMESPACE } from '../constants'
import { useTranslation } from 'react-i18next'
const cx = classnames.bind(styles)

const ExposureTooltipRow = ({
  rowKey,
  rowValue,
}: {
  rowKey: string
  rowValue: React.ReactNode
}) => (
  <RowDeprecated className={cx('ExposureTooltipRow')}>
    <Typography color="black">{rowKey}: </Typography>
    <Typography color="black" className={cx('ExposureTooltipValue')}>
      {rowValue}
    </Typography>
  </RowDeprecated>
)

const ExposureTooltipComponent: React.ComponentType<
  TreemapTooltipComponentProps<ExposureDataItem>
> = ({ data }) => {
  const { t } = useInternationalizationCore()

  if (!data.name) {
    return null
  }

  // Determine if this is a root item
  const isRoot = data.children && data.children.length > 0

  // Parse name for children
  let itemName = data.name
  let fractionInCategory = ''

  if (!isRoot && data.name) {
    const splittedName = data.name.split(' ')
    const childrenCategoryName = splittedName.slice(0, -1).join(' ')
    fractionInCategory = splittedName.slice(-1).join('')
    itemName = childrenCategoryName
  }

  return (
    <div className={cx('ExposureTooltip')}>
      <RowDeprecated gap={0.5}>
        <Score value={data.score} />
        <Typography color="black" variant="body1">
          {itemName}
        </Typography>
      </RowDeprecated>

      {isRoot ? (
        <div className={cx('ExposureTooltipList')}>
          <ExposureTooltipRow
            rowKey={t('fraction')}
            rowValue={`${formatNumber(data.value * 100, 1)}%`}
          />
        </div>
      ) : (
        <div className={cx('ExposureTooltipList')}>
          <ExposureTooltipRow
            rowKey={`${t('fractionIn')} ${itemName}`}
            rowValue={fractionInCategory}
          />
          <ExposureTooltipRow
            rowKey={t('overallFraction')}
            rowValue={`${formatNumber(data.value * 100, 1)}%`}
          />
        </div>
      )}
    </div>
  )
}

const ExposureTooltip: React.ComponentType<
  TreemapTooltipComponentProps<ExposureDataItem>
> = ({ data }) => {
  const { t } = useTranslation()

  return (
    <ThemeProvider theme={enterprise['light']}>
      <InternationalizationProviderCore
        t={(key) => t(`${CORE_LANG_NAMESPACE}:${key}`)}
      >
        <ExposureTooltipComponent data={data} />
      </InternationalizationProviderCore>
    </ThemeProvider>
  )
}

// Extended data type for Exposure chart
interface ExposureDataItem extends TreemapDataItem {
  score: number
  link?: string
}

interface ExposureChartProps {
  data?: ExposureDataItem[]
  loading?: boolean
  className?: string
}

const ExposureChart = React.memo(
  ({ data, loading, className }: ExposureChartProps) => {
    return (
      <TreemapChart
        data={data}
        loading={loading}
        className={className}
        style={{ height: 300 }}
        chartOptions={{
          visibleMin: 1,
          roam: false,
          left: 4,
          top: 4,
          right: 4,
          bottom: 4,
          squareRatio: 16 / 9,
          breadcrumb: {
            show: false,
            emptyItemWidth: 0,
          },
          itemStyle: {
            gapWidth: 4,
            borderWidth: 2,
            borderColor: '#fff',
          },
        }}
        rootItemOptions={(item) => ({
          itemStyle: {
            color: item.color,
            gapWidth: -1,
            borderColor: item.color,
          },
          upperLabel: {
            show: true,
            height: 28,
            fontSize: 14,
            color: '#0F131B',
            width: '100%',
            textBorderColor: 'none',
            lineOverflow: 'truncate',
          },
          label: {
            fontSize: 14,
            color: '#0F131B',
            formatter: ({ name, value }) =>
              `${name} ${formatNumber(Number(value) * 100, 1)}%`,
          },
        })}
        childItemOptions={(child) => ({
          itemStyle: {
            color: child.color,
            borderWidth: 1,
            borderColor: '#374766',
          },
          label: {
            fontSize: 14,
            color: '#0F131B',
            formatter: ({ name }) => name,
          },
        })}
        tooltipComponent={ExposureTooltip}
      />
    )
  }
)

export default ExposureChart
ExposureChart.displayName = 'ExposureChart'
